/**
 * inbox — the ONE guarded `setItems` inbox, shared by every component that
 * carries an optimistic slot (README §5 immediate-ui-response).
 *
 * `NoticeList` first grew the guard (a status row driven by the TAP instead of
 * the feed); `MediaPicker` needs the SAME guard for its optimistic loading
 * photos. Two copies of a focus/until/seen guard are two guards that can drift,
 * so both callers read through `guardedInbox` here, and the Flutter twin
 * (`components/shared.dart` `viewIrGuardedInbox`) mirrors it line for line.
 *
 * A delivery carries up to two OPTIONAL guard keys; carrying neither is exactly
 * the `{items}` the slot has always declared.
 *
 *  FOCUS — the slot inbox OUTLIVES THE PAGE on Flutter (`ViewIRPage` never
 *  remounts, `_slots` is controller-level, nothing dispatches a reset), so a
 *  delivery made on document A is still sitting there when document B's detail
 *  page renders. The delivery therefore NAMES the row it was made for
 *  (`"focus": {"$": "focusEntry.id"}`) and the kit honors it only while that is
 *  the row on screen — a GUARD, not a blanket clear: coming back to document A
 *  must still show the line its own tap put there.
 *
 *  UNTIL/SEEN — an optimistic line has to END when the thing it announced
 *  arrives, and the only honest evidence of that is the FEED. The delivery
 *  carries the path to watch (`"until": "focusEntry.images"`) and what that path
 *  held AT THE TAP (`"seen": {"$": "focusEntry.images"}`); the kit re-reads the
 *  path on every render and drops the delivery the moment it has GROWN. So the
 *  placeholder is REPLACED by the real row rather than standing beside it, and
 *  neither kit needs a clock or a memory of its own.
 */
import { getPath } from './paths';

/** How many things a reading holds — a list's length, a number as itself. */
export function countOf(v) {
  if (v == null) return 0;
  if (Array.isArray(v)) return v.length;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') return v.trim() === '' ? 0 : 1;
  if (typeof v === 'object') return Object.keys(v).length ? 1 : 0;
  return 1;
}

/**
 * THE ROW THIS PAGE IS ABOUT, in the host's own identity vocabulary — the same
 * ladder `focusEntry` itself is resolved by (README §4.3).
 */
export function focusToken(ctx) {
  const f = ctx?.focusEntry;
  if (!f || typeof f !== 'object') return null;
  return f.id ?? f.item_id ?? f.entry_id ?? f.log_card_id ?? null;
}

/**
 * The guarded `setItems` payload, or null when a guard rejects it (the caller
 * then falls back to props/bind). Returns the payload OBJECT itself — callers
 * read `.items` / `.loading` off it — so its identity is the slot's, stable
 * across renders until the slot changes.
 */
export function guardedInbox(slots, ctx, slot = 'setItems') {
  const inbox = slots?.[slot]?.payload;
  if (!inbox || typeof inbox !== 'object' || Array.isArray(inbox)) return null;
  const focus = inbox.focus;
  if (focus != null && focus !== '' && String(focus) !== String(focusToken(ctx))) return null;
  const until = typeof inbox.until === 'string' ? inbox.until.trim() : '';
  if (until && countOf(getPath(ctx, until)) > countOf(inbox.seen)) return null;
  return inbox;
}
